import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const HIGH_SCORE_KEY = 'frontdesk-404-highscore';

interface FallingItem {
  id: number;
  x: number;
  y: number;
  speed: number;
  emoji: string;
  isBomb: boolean;
}

const ITEM_EMOJIS = ['📅', '✉', '$', '✓'];

export function NotFoundPage() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(() => {
    if (typeof window === 'undefined') return 0;
    return Number(localStorage.getItem(HIGH_SCORE_KEY) ?? 0);
  });
  const [playing, setPlaying] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [paddleX, setPaddleX] = useState(0.5); // 0–1 normalized
  const itemsRef = useRef<FallingItem[]>([]);
  const livesRef = useRef(3);
  const [lives, setLives] = useState(3);
  const nextIdRef = useRef(1);
  const lastSpawnRef = useRef(0);

  function start() {
    setScore(0);
    setLives(3);
    livesRef.current = 3;
    itemsRef.current = [];
    setGameOver(false);
    setPlaying(true);
    lastSpawnRef.current = performance.now();
  }

  // Save high score
  useEffect(() => {
    if (gameOver && score > highScore) {
      setHighScore(score);
      localStorage.setItem(HIGH_SCORE_KEY, String(score));
    }
  }, [gameOver, score, highScore]);

  // Game loop
  useEffect(() => {
    if (!playing) return;
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext('2d')!;
    let raf = 0;
    let last = performance.now();
    const PADDLE_W = 90;
    const PADDLE_H = 16;

    function frame(now: number) {
      const dt = Math.min(64, now - last);
      last = now;

      // Spawn
      if (now - lastSpawnRef.current > Math.max(280, 900 - score * 8)) {
        const isBomb = Math.random() < 0.18;
        itemsRef.current.push({
          id: nextIdRef.current++,
          x: Math.random() * (canvas.width - 40) + 20,
          y: -20,
          speed: 0.12 + Math.random() * 0.12 + score * 0.0025,
          emoji: isBomb ? '💣' : ITEM_EMOJIS[Math.floor(Math.random() * ITEM_EMOJIS.length)],
          isBomb,
        });
        lastSpawnRef.current = now;
      }

      // Update
      const paddleCenterX = paddleX * canvas.width;
      const paddleY = canvas.height - 30;
      const next: FallingItem[] = [];
      let scoreDelta = 0;
      let livesDelta = 0;
      for (const it of itemsRef.current) {
        const ny = it.y + it.speed * dt;
        const caught = ny + 16 >= paddleY && ny <= paddleY + PADDLE_H && Math.abs(it.x - paddleCenterX) < PADDLE_W / 2 + 12;
        if (caught) {
          if (it.isBomb) livesDelta -= 1;
          else scoreDelta += 1;
          continue;
        }
        if (ny > canvas.height + 30) {
          if (!it.isBomb) livesDelta -= 1;
          continue;
        }
        next.push({ ...it, y: ny });
      }
      itemsRef.current = next;
      if (scoreDelta) setScore((s) => s + scoreDelta);
      if (livesDelta) {
        livesRef.current += livesDelta;
        setLives(livesRef.current);
        if (livesRef.current <= 0) {
          setPlaying(false);
          setGameOver(true);
          return;
        }
      }

      // Draw
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Paddle
      ctx.fillStyle = '#3b82f6';
      ctx.fillRect(paddleCenterX - PADDLE_W / 2, paddleY, PADDLE_W, PADDLE_H);

      // Items
      ctx.font = '24px system-ui';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      for (const it of itemsRef.current) {
        ctx.fillText(it.emoji, it.x, it.y);
      }

      raf = requestAnimationFrame(frame);
    }

    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [playing, paddleX, score]);

  // Input handlers
  useEffect(() => {
    if (!playing) return;
    const canvas = canvasRef.current!;
    function handleMove(e: MouseEvent | TouchEvent) {
      const rect = canvas.getBoundingClientRect();
      const x = 'touches' in e ? e.touches[0].clientX : e.clientX;
      setPaddleX(Math.max(0, Math.min(1, (x - rect.left) / rect.width)));
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'ArrowLeft') setPaddleX((p) => Math.max(0, p - 0.05));
      if (e.key === 'ArrowRight') setPaddleX((p) => Math.min(1, p + 0.05));
    }
    canvas.addEventListener('mousemove', handleMove);
    canvas.addEventListener('touchmove', handleMove);
    window.addEventListener('keydown', handleKey);
    return () => {
      canvas.removeEventListener('mousemove', handleMove);
      canvas.removeEventListener('touchmove', handleMove);
      window.removeEventListener('keydown', handleKey);
    };
  }, [playing]);

  return (
    <div style={{ minHeight: '100vh', background: '#0f172a', color: 'white', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 16, fontFamily: 'system-ui, sans-serif' }}>
      <h1 style={{ fontSize: 96, margin: 0, fontWeight: 800, letterSpacing: -4 }}>404</h1>
      <p style={{ fontSize: 20, margin: '0 0 24px', color: '#94a3b8' }}>This page wandered off. Catch some bookings while you're here.</p>
      <div style={{ position: 'relative' }}>
        <canvas
          ref={canvasRef}
          width={520}
          height={400}
          style={{
            background: '#0f172a',
            border: '2px solid #1e293b',
            borderRadius: 12,
            cursor: 'none',
            maxWidth: '100%',
            touchAction: 'none',
          }}
        />
        {!playing && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(15, 23, 42, 0.9)',
              borderRadius: 12,
            }}
          >
            <p style={{ fontSize: 16, marginBottom: 8 }}>
              {gameOver ? `Game over. Score: ${score}` : 'Catch 📅 ✉ $ ✓ — avoid 💣'}
            </p>
            <p style={{ fontSize: 14, color: '#94a3b8', marginTop: 0 }}>High score: {highScore}</p>
            <button
              onClick={start}
              style={{
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                fontSize: 16,
                borderRadius: 8,
                cursor: 'pointer',
                marginTop: 16,
              }}
            >
              {gameOver ? 'Play again' : 'Start'}
            </button>
          </div>
        )}
      </div>
      <div style={{ marginTop: 16, display: 'flex', gap: 32, fontSize: 14 }}>
        <span>Score: <strong>{score}</strong></span>
        <span>Lives: <strong>{'❤️'.repeat(Math.max(0, lives))}</strong></span>
        <span>Best: <strong>{highScore}</strong></span>
      </div>
      <Link
        to="/app"
        style={{
          marginTop: 24,
          color: '#94a3b8',
          fontSize: 14,
          textDecoration: 'underline',
        }}
      >
        Take me home
      </Link>
    </div>
  );
}
